import Button from '@components/Button/Button';
import Input from '@components/Input/Input';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import useValidateInput, { notNullValidator } from '@hooks/use-validate-input';
import { useRouter } from 'next/router';
import { type FC, useState } from 'react';

import {
  MobileOnboardingInputsGroup,
  MobileOnboardingInputsSection,
  MobileOnboardingInputsSectionHeader,
  MobileOnboardingInputsSectionHeaderContainer,
  MobileOnboardingNextButtonContainer,
} from './MobileOnboardingInputs';
import styles from './OnboardingNameDetailsPageForMobile.module.scss';
import { type CommonOnboardingDetailsPageForMobileProps, MobileDetailsSteps } from './types';

type OnboardingNameDetailsPageForMobileProps = Omit<
  CommonOnboardingDetailsPageForMobileProps,
  'uploadEmailSignatureFieldImagesThunk' | 'handleErrorUploadingImage'
>;

const OnboardingNameDetailsPageForMobile: FC<
  React.PropsWithChildren<OnboardingNameDetailsPageForMobileProps>
> = ({ emailSignature, emailSignatureDispatch, handleSave }) => {
  const router = useRouter();
  const [firstNameInputNode, firstNameInputRef] = useNode();
  const [isFirstNameError] = useValidateInput(emailSignature.personalDetails.firstName, [notNullValidator]);
  const [, , firstNameInputBlurred] = useOnFocus(firstNameInputNode);
  const [showSubmitErrors, setShowSubmitErrors] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  function onFirstNameChange(updatedFirstName: string) {
    emailSignatureDispatch({ type: 'UPDATE_FIRST_NAME', value: updatedFirstName });
  }

  function onLastNameChange(updatedLastName: string) {
    emailSignatureDispatch({ type: 'UPDATE_LAST_NAME', value: updatedLastName });
  }

  async function onClickNext() {
    if (!emailSignature.personalDetails.firstName) {
      firstNameInputNode?.focus();
      setShowSubmitErrors(true);
      return;
    }

    setIsSaving(true);
    await handleSave(emailSignature, (savedEmailSignature) => {
      router.push(`/onboarding/email-signature/details/${MobileDetailsSteps.OCCUPATION_DETAILS}`, undefined, {
        shallow: true,
      });
      setIsSaving(false);
      emailSignatureDispatch({ type: 'IMPORT_EMAIL_SIGNATURE', value: savedEmailSignature });
    });
  }

  return (
    <MobileOnboardingInputsSection>
      <MobileOnboardingInputsSectionHeaderContainer>
        <MobileOnboardingInputsSectionHeader>
          Let&apos;s start with your name
        </MobileOnboardingInputsSectionHeader>
      </MobileOnboardingInputsSectionHeaderContainer>

      <MobileOnboardingInputsGroup>
        <Input
          ref={firstNameInputRef}
          label="First name"
          type="text"
          value={emailSignature.personalDetails.firstName}
          onChange={onFirstNameChange}
          autoComplete="given-name"
          errorMessage="First name is required"
          showError={
            (isFirstNameError && firstNameInputBlurred) ||
            (showSubmitErrors && !emailSignature.personalDetails.firstName)
          }
          enableClearButton
        />

        <Input
          label="Last name"
          type="text"
          value={emailSignature.personalDetails.lastName}
          onChange={onLastNameChange}
          autoComplete="family-name"
          hideAllErrorMessages
          enableClearButton
        />
      </MobileOnboardingInputsGroup>
      <div className={styles['legal-disclaimer']}>
        By continuing, you agree to our{' '}
        <a
          className={styles['legal-disclaimer-link']}
          href="https://blinq.me/legal/privacy-policy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          className={styles['legal-disclaimer-link']}
          href="https://blinq.me/legal/terms-conditions"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Service
        </a>
      </div>
      <MobileOnboardingNextButtonContainer>
        <Button size="large" onClick={onClickNext} loading={isSaving}>
          Next
        </Button>
      </MobileOnboardingNextButtonContainer>
    </MobileOnboardingInputsSection>
  );
};

export default OnboardingNameDetailsPageForMobile;
